import '@fontsource-variable/google-sans-flex/rond.css';
import '@fontsource/google-sans-code/400.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Button, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import PauseCircleOutlineRounded from '@mui/icons-material/PauseCircleOutlineRounded';
import TravelExploreRounded from '@mui/icons-material/TravelExploreRounded';
import { App } from './App';
import { createAuthAdapter } from './auth';
import { StatusScreen } from './components/StatusScreen';
import { env } from './config/env';
import { themeOptionsFor } from './tenant/applyBranding';
import { loadTenantConfig } from './tenant/loadTenantConfig';
import { createAppTheme } from './theme';
import { rememberReferral } from './referrals/capture';
import { loadFont } from './theme/fonts';

const root = createRoot(document.getElementById('root')!);

function renderStandalone(screen: React.ReactNode) {
  root.render(
    <ThemeProvider theme={createAppTheme()} defaultMode="system">
      <CssBaseline enableColorScheme />
      {screen}
    </ThemeProvider>,
  );
}

/**
 * hostname → tenant → branding → auth → app. One build serves every tenant:
 * nothing about the customer is known until the API answers for this host.
 */
async function bootstrap() {
  document.title = env.platformName;
  rememberReferral();
  const result = await loadTenantConfig();

  if (result.kind === 'redirect') {
    window.location.replace(result.to);
    return;
  }
  if (result.kind === 'not-found') {
    renderStandalone(
      <StatusScreen
        icon={<TravelExploreRounded color="primary" sx={{ fontSize: 48 }} />}
        title="Endereço não encontrado"
        description={`Não existe nenhum espaço ativo em ${window.location.hostname}. Confira o endereço que você recebeu.`}
      />,
    );
    return;
  }
  if (result.kind === 'inactive') {
    renderStandalone(
      <StatusScreen
        icon={<PauseCircleOutlineRounded color="warning" sx={{ fontSize: 48 }} />}
        title={
          result.status === 'suspended' ? 'Espaço temporariamente indisponível' : 'Espaço encerrado'
        }
        description={
          result.status === 'suspended'
            ? `O acesso a ${result.name} está suspenso no momento. Se você é cliente, fale com o seu profissional.`
            : `${result.name} não está mais disponível neste endereço. Se você é cliente, fale com o seu profissional.`
        }
      />,
    );
    return;
  }
  if (result.kind === 'unavailable') {
    console.error(result.error);
    renderStandalone(
      <StatusScreen
        icon={<CloudOffRounded color="error" sx={{ fontSize: 48 }} />}
        title="Serviço indisponível"
        description="Não conseguimos falar com o servidor agora. Tente novamente em instantes."
        actions={
          <Button variant="contained" onClick={() => window.location.reload()}>
            Tentar de novo
          </Button>
        }
      />,
    );
    return;
  }

  const [adapter] = await Promise.all([
    createAuthAdapter(),
    loadFont(themeOptionsFor(result.config).font),
  ]);
  root.render(
    <StrictMode>
      <App initialConfig={result.config} adapter={adapter} />
    </StrictMode>,
  );
}

void bootstrap();
